// api/client.js
class APIClient {
    constructor(config = {}) {
      this.baseURL = config.baseURL;
      this.timeout = config.timeout || 10000;
      this.headers = {
        'Content-Type': 'application;/json',
        ...config.headers
      };

      // Auth token storage
      this.token = config.token || this.getStoredToken();

      // Request/Response interceptors
      this.requestInterceptors = [];
      this.responseInterceptors = [];

      // Setup default interceptors
      this.setupDefaultInterceptors();
    }

    // Token management
    setToken(token) {
      this.token = token;
      if (typeof window !== 'undefined') {
        localStorage.setItem('auth_token', token);
      }
    }

    getStoredToken() {
      if (typeof window !== 'undefined') {
        return localStorage.getItem('auth_token');
      }
      return null;
    }

    clearToken() {
      this.token = null;
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth_token');
      }
    }

    // Interceptor management
    addRequestInterceptor(interceptor) {
      this.requestInterceptors.push(interceptor);
    }

    addResponseInterceptor(interceptor) {
      this.responseInterceptors.push(interceptor);
    }

    setupDefaultInterceptors() {
      // Default request interceptor - adds auth header
      this.addRequestInterceptor((config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      });

      // Default response interceptor - handles common errors
      this.addResponseInterceptor({
        success: (response) => response,
        error: (error) => {
          if (error.status === 401) {
            this.clearToken();
            // Redirect to login or emit event
            this.onUnauthorized?.(error);
          }
          return Promise.reject(error);
        }
      });
    }

    // Apply interceptors
    async applyRequestInterceptors(config) {
      let modifiedConfig = { ...config };
      for (const interceptor of this.requestInterceptors) {
        modifiedConfig = await interceptor(modifiedConfig);
      }
      return modifiedConfig;
    }

    async applyResponseInterceptors(response, isError = false) {
      let result = response;
      for (const interceptor of this.responseInterceptors) {
        try {
          if (isError && interceptor.error) {
            result = await interceptor.error(result);
          } else if (!isError && interceptor.success) {
            result = await interceptor.success(result);
          }
        } catch (err) {
          result = err;
          isError = true;
        }
      }
      return result;
    }

    // Core request method
    async request(config) {
      try {
        // Apply request interceptors
        const requestConfig = await this.applyRequestInterceptors({
          method: 'GET',
          headers: { ...this.headers },
          ...config
        });

        // Build URL
        const url = requestConfig.url.startsWith('http')
          ? requestConfig.url
          : `${this.baseURL}${requestConfig.url}`;

        // Setup fetch options
        const fetchOptions = {
          method: requestConfig.method,
          headers: requestConfig.headers,
          signal: AbortSignal.timeout(this.timeout)
        };

        // Add body for non-GET requests
        if (requestConfig.data && requestConfig.method !== 'GET') {
          fetchOptions.body = typeof requestConfig.data === 'string'
            ? requestConfig.data
            : JSON.stringify(requestConfig.data);
        }

        // Add query params for GET requests
        if (requestConfig.params && requestConfig.method === 'GET') {
          const searchParams = new URLSearchParams(requestConfig.params);
          const separator = url.includes('?') ? '&' : '?';
          url += separator + searchParams.toString();
        }

        // Make the request
        const response = await fetch(url, fetchOptions);

        // Parse response
        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          data = await response.text();
        }

        const result = {
          data,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          config: requestConfig
        };

        if (!response.ok) {
          const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
          error.response = result;
          error.status = response.status;
          throw error;
        }

        // Apply response interceptors
        return await this.applyResponseInterceptors(result);

      } catch (error) {
        // Apply error interceptors
        const processedError = await this.applyResponseInterceptors(error, true);
        throw processedError;
      }
    }

    // Convenience methods
    get(url, config = {}) {
      return this.request({ ...config, method: 'GET', url });
    }

    post(url, data, config = {}) {
      return this.request({ ...config, method: 'POST', url, data });
    }

    put(url, data, config = {}) {
      return this.request({ ...config, method: 'PUT', url, data });
    }

    patch(url, data, config = {}) {
      return this.request({ ...config, method: 'PATCH', url, data });
    }

    delete(url, config = {}) {
      return this.request({ ...config, method: 'DELETE', url });
    }

    // File upload helper
    async uploadFile(url, file, config = {}) {
      const formData = new FormData();
      formData.append('file', file);

      // Add additional fields if provided
      if (config.fields) {
        Object.entries(config.fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }

      return this.request({
        ...config,
        method: 'POST',
        url,
        data: formData,
        headers: {
          // Remove Content-Type to let browser set it with boundary
          ...config.headers,
          'Content-Type': undefined
        }
      });
    }

    // Batch requests
    async batch(requests) {
      try {
        const promises = requests.map(req => this.request(req));
        return await Promise.allSettled(promises);
      } catch (error) {
        throw new Error(`Batch request failed: ${error.message}`);
      }
    }

    // Health check
    async healthCheck() {
      try {
        const response = await this.get('/health');
        return response.data;
      } catch (error) {
        throw new Error(`Health check failed: ${error.message}`);
      }
    }
  }

  class LocationService {
    constructor(apiClient) {
      this.api = apiClient;
    }

    async validatePostalCode(postalCode) {
      const response = await this.api.get(`/api/location/validate/${postalCode}`);
      return response.data;
    }

    async getFranchise(id) {
      const response = await this.api.get(`/api/location/${id}`);
      return response.data;
    }

    async getFranchisePromotions(id) {
      const response = await this.api.get(`/api/location/${id}/discounts`);
      return response.data;
    }

    async getHolidays(franchiseId) {
      const response = await this.api.get(`/api/location/${franchiseId}/holidays`);
      return response.data;
    }

  }

  // Main API instance
  const apiClient = new APIClient({
    baseURL: `${window.location.protocol}//${window.location.host}`,
    timeout: 15000,
    headers: {
      'X-App-Version': '1.0.0'
    }
  });

  // Service instances
  export const locationService = new LocationService(apiClient);

  // Export the main client
  export default apiClient;

  // Error types for better error handling
  export class APIError extends Error {
    constructor(message, status, response) {
      super(message);
      this.name = 'APIError';
      this.status = status;
      this.response = response;
    }
  }

  export class NetworkError extends Error {
    constructor(message) {
      super(message);
      this.name = 'NetworkError';
    }
  }

  export class TimeoutError extends Error {
    constructor(message) {
      super(message);
      this.name = 'TimeoutError';
    }
  }
